// Story 8.14 (ADO-2023), D-6: whether the order of a cart needs the store's own reference, and
// how long one may be, for the cart's reference field.
//
//   const rule = useOrderReferenceRule(cart);   // { required, maxLength } | null
//   const rule = await loadOrderReferenceRule(cart);   // the same answer, awaited (8.6)
//
// Story 8.4 (ADO-2013), D-1: the rule belongs to the Trading Account, so it is read for the
// cart (the depot AND the account; staff name none) and cached per cart (`cartTargetKey`).
//
// Behaviour:
//   - One read per cart for the session: the answer is cached per cart in a small store, so
//     every consumer, a remount and a switch back to the cart reuse it. Two consumers mounting
//     together share one request.
//   - It returns `null` ("unknown") while the cart's first answer loads, when the read fails,
//     and with no cart. The field then claims neither "optional" nor "required", and sets no
//     maximum: the send enforces the rule itself (HBR-08). A failed read is not cached, so a
//     later mount reads again.
//   - Answers are keyed by the cart they were read for, and the hook returns only the current
//     cart's, so a slow answer for cart A never paints on cart B.
//   - `loadOrderReferenceRule` is the same read for a caller that needs the answer before it acts
//     (Story 8.6, ADO-2015: "Submit now" on the choice screen): the cached answer, else the read in
//     flight, else a new read, cached on success. It resolves `null` when the read fails.

import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { getOrderReferenceRule, type OrderReferenceRule } from '../api/basket.client';
import { cartTargetKey, type CartTarget } from '../api/cart-target';

interface OrderReferenceRuleState {
  /** The answer per cart key (`cartTargetKey`). */
  readonly rules: Readonly<Record<string, OrderReferenceRule>>;
}

const useOrderReferenceRuleStore = create<OrderReferenceRuleState>(() => ({ rules: {} }));

/** The read in flight per cart key, so consumers mounting together share one request. */
const inFlight = new Map<string, Promise<OrderReferenceRule | null>>();
/** Bumped by every reset: a read that resolves after it caches nothing. */
let generation = 0;

/** The cart's rule: the cached answer, else the read in flight, else a new read (cached on
 *  success). `null` when the read fails. */
export function loadOrderReferenceRule(cart: CartTarget): Promise<OrderReferenceRule | null> {
  const cartKey = cartTargetKey(cart);
  const cached = useOrderReferenceRuleStore.getState().rules[cartKey];
  if (cached !== undefined) return Promise.resolve(cached);
  const pending = inFlight.get(cartKey);
  if (pending !== undefined) return pending;
  const startedIn = generation;
  const read = getOrderReferenceRule(cart)
    .then(
      (rule) => {
        if (startedIn === generation) {
          useOrderReferenceRuleStore.setState((state) => ({
            rules: { ...state.rules, [cartKey]: rule },
          }));
        }
        return rule;
      },
      () => null, // unknown; not cached, so a later mount reads again
    )
    .finally(() => {
      if (startedIn === generation) inFlight.delete(cartKey);
    });
  inFlight.set(cartKey, read);
  return read;
}

export function useOrderReferenceRule(cart: CartTarget | null): OrderReferenceRule | null {
  const cartKey = cart === null ? null : cartTargetKey(cart);
  // A new object for the same cart is the same read: the effect re-runs on the key alone.
  const cartRef = useRef(cart);
  cartRef.current = cart;
  useEffect(() => {
    if (cartKey !== null && cartRef.current !== null) void loadOrderReferenceRule(cartRef.current);
  }, [cartKey]);
  return useOrderReferenceRuleStore((state) =>
    cartKey === null ? null : (state.rules[cartKey] ?? null),
  );
}

/** Forget every cached rule (tests; the suite runs with `isolate: false`). */
export function resetOrderReferenceRules(): void {
  generation += 1;
  inFlight.clear();
  useOrderReferenceRuleStore.setState({ rules: {} });
}
